import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { staffingAPI } from '@/services/staffingAPI';
import { authAPI } from '@/services/authAPI';
import { useAuthStore } from '@/store/authStore';
import { Mail, Lock, Phone, Building2, Globe, ArrowLeft, Loader2, ChevronRight } from 'lucide-react';
import PasswordInput from '@/components/shared/PasswordInput';
import ThemeToggle from '@/components/shared/ThemeToggle';

export default function StaffingSignup() {
  const navigate = useNavigate();
  const setAuthState = useAuthStore.setState;
  const [form, setForm] = useState({ org_name: '', org_domain: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ terms_version: '', privacy_version: '' });

  useEffect(() => {
    authAPI.getLegalVersions().then(setLegalVersions).catch(() => { /* ignore */ });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      const r = await staffingAPI.signup({
        ...form,
        consent_terms_version: legalVersions.terms_version || null,
        consent_privacy_version: legalVersions.privacy_version || null,
      });
      localStorage.setItem('auth_token', r.access_token);
      setAuthState({ token: r.access_token, user: r.user, isAuthenticated: true, isLoading: false });
      navigate('/staffing', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6 py-10" data-testid="staffing-signup-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm mb-8"><ArrowLeft className="w-4 h-4" /> Back to sign in</Link>

        <h1 className="text-3xl font-bold mb-2" style={{fontFamily: "'DM Serif Display', serif"}}>
          <span className="text-slate-900 dark:text-white">Onboard your </span>
          <span className="text-emerald-600 dark:text-emerald-400">staffing org</span>
        </h1>
        <p className="text-slate-500 dark:text-zinc-500 mb-6 text-sm">Submit your domain for admin approval. Once approved, you can bulk-upload candidate resumes and start matching.</p>

        <form onSubmit={onSubmit} className="space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 px-4 py-2.5 rounded-lg text-sm" data-testid="signup-error">{error}</div>}

          <Field icon={Building2} label="Organization name" value={form.org_name} onChange={v => setForm({...form, org_name: v})} placeholder="Acme Staffing" testId="org-name-input" required />
          <Field icon={Globe} label="Organization domain" value={form.org_domain} onChange={v => setForm({...form, org_domain: v.toLowerCase()})} placeholder="acmestaff.com" testId="org-domain-input" required />
          <Field icon={Mail} label="Your work email" type="email" value={form.email} onChange={v => setForm({...form, email: v.toLowerCase()})} placeholder="you@acmestaff.com" testId="signup-email-input" required />
          <div>
            <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Password</label>
            <PasswordInput
              value={form.password}
              onChange={v => setForm({...form, password: v})}
              onChangeValue
              testId="signup-password-input"
              placeholder="Min 6 chars"
              required
              minLength={6}
              autoComplete="new-password"
              icon={Lock}
              className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-11 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 text-sm"
            />
          </div>
          <Field icon={Phone} label="Phone (optional)" value={form.phone} onChange={v => setForm({...form, phone: v})} placeholder="+1 555 0100" testId="signup-phone-input" />

          <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="staffing-tos-accept-row">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 accent-emerald-500 h-4 w-4 flex-shrink-0"
              data-testid="staffing-tos-accept-checkbox"
            />
            <span className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
              I have read and agree to Numuv&apos;s{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="staffing-tos-terms-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="staffing-tos-privacy-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Privacy Policy</Link>,
              and confirm I am authorized to bind this organization to these terms.
            </span>
          </label>

          <button type="submit" disabled={loading || !acceptedTerms} data-testid="signup-submit-btn"
            className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit for approval <ChevronRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="text-xs text-slate-500 dark:text-zinc-600 mt-6 text-center">
          Your email domain must match the org domain.
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, type = 'text', placeholder, testId, required }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
          className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 text-sm" />
      </div>
    </div>
  );
}
